/**
 * Scene loader plugin for .trace format.
 *
 * .trace files are ZIP archives containing:
 * - scene.json: Scene definition with SceneData array
 * - assets/: Folder with GLB and other asset files
 *
 * Transform handling (version-gated):
 * - v2.0+: GLB root transforms are preserved. The scene transform represents
 *   user intent; the GLB root provides model orientation (e.g. Blender Z-up).
 * - v1.x and earlier (legacy ContentSDK): GLB root transforms are discarded.
 *   Unity's importer pre-baked GLB roots into scene transforms, so the scene
 *   transform is the sole source of truth.
 */

import JSZip from 'jszip';
import { BaseTracePlugin, ParsedSceneStructure, ParsedSceneNode } from './BaseTracePlugin';
import type { ISceneLoaderPluginExtensions } from "@babylonjs/core/Loading/sceneLoader";
import { Utility } from '../../utils/Utility';

// =============================================================================
// Types
// =============================================================================

/** scene.json structure */
interface TraceSceneJson {
    sceneName?: string;
    version?: string;
    content: TraceSceneContent[];
    /** Maps script @type names to asset paths (e.g., { "Wobble": "./assets/wobble.ts" }) */
    scripts?: Record<string, string>;
}

interface TraceSceneContent {
    name: string;
    src?: string;
    active?: boolean;
    transform?: {
        translation?: [number, number, number];
        rotation?: [number, number, number, number];
        scale?: [number, number, number];
        parent?: string;
        transition?: { duration: number; easing?: string; delay?: number };
    };
    /** Behaviors/components - supports both field names for compatibility */
    behaviors?: unknown[];
    components?: unknown[];
}

// =============================================================================
// Plugin Implementation
// =============================================================================

export class TraceSceneLoaderPlugin extends BaseTracePlugin {
    readonly name = "trace";
    readonly extensions: ISceneLoaderPluginExtensions = {
        ".trace": { isBinary: true },
    };

    /**
     * Parse .trace ZIP archive into common scene structure
     */
    protected async parseSceneData(
        data: unknown,
        rootUrl: string,
        _fileName?: string
    ): Promise<ParsedSceneStructure> {
        // Load ZIP archive
        const zip = await JSZip.loadAsync(data as ArrayBuffer);

        // Read scene.json
        const sceneFile = zip.file('scene.json');
        if (!sceneFile) {
            throw new Error('Archive does not contain scene.json');
        }
        const sceneJson = Utility.parseLegacyJson(await sceneFile.async('string')) as TraceSceneJson;

        // Extract assets to blobs for ResourceManager storage
        const assetBlobs = new Map<string, Blob>();
        const assetsFolder = zip.folder('assets');

        if (assetsFolder) {
            const assetFiles = assetsFolder.filter((_relativePath, file) => !file.dir);
            for (const file of assetFiles) {
                const arrayBuffer = await file.async('arraybuffer');
                // Extract just the filename (remove 'assets/' prefix)
                const assetFileName = file.name.replace(/^assets\//, '');
                const mimeType = Utility.mimeTypeFromFilename(assetFileName);
                const blob = new Blob([arrayBuffer], mimeType ? { type: mimeType } : undefined);

                // Store blob for ResourceManager (keyed by path like "assets/filename.jpg")
                assetBlobs.set(`assets/${assetFileName}`, blob);
            }
        }

        // Legacy ContentSDK scenes (v1.x): Unity pre-baked GLB root transforms
        // into scene transforms, so we must discard the GLB root on load.
        // v2.0+ scenes preserve GLB roots (model orientation is separate from user intent).
        const majorVersion = parseInt(sceneJson.version ?? '0', 10);
        const resetGlbRoot = Number.isNaN(majorVersion) || majorVersion < 2;

        // Convert scene.json content to ParsedSceneNode array
        // All asset loading is handled by behaviors (Model, Image, Video)
        // which load from ResourceManager using the src path
        const nodes: ParsedSceneNode[] = (sceneJson.content ?? []).map(item => ({
            name: item.name,
            src: item.src,
            transform: item.transform ? {
                translation: item.transform.translation,
                rotation: item.transform.rotation,
                scale: item.transform.scale,
            } : undefined,
            parentName: item.transform?.parent,
            active: item.active,
            behaviors: item.behaviors ?? item.components,
            resetAssetTransform: resetGlbRoot && item.src ? true : undefined,
        }));

        return {
            nodes,
            assetBlobs,
            rootUrl,
            scripts: sceneJson.scripts,
        };
    }
}
